// state/campaigns.selectors.ts
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { campaignsAdapter, CampaignsState } from './reducer';
import { SelectedCampaignVM } from '../../types/campaigns.models';

export const selectCampaignsState =
  createFeatureSelector<CampaignsState>('campaigns');

const { selectAll, selectEntities } = campaignsAdapter.getSelectors();

export const selectAllCampaigns = createSelector(
  selectCampaignsState,
  selectAll // mantiene el orden de inserción (que viene pre-ordenado)
);

export const selectCampaignEntities = createSelector(
  selectCampaignsState,
  selectEntities
);

export const selectSelectedId = createSelector(
  selectCampaignsState,
  (s) => s.selectedId
);

export const selectSelectionSource = createSelector(
  selectCampaignsState,
  (s) => s.source
);

export const selectSelectedCampaign = createSelector(
  selectCampaignEntities,
  selectSelectedId,
  (entities, id) => (id ? entities[id] ?? null : null)
);

export const selectSelectedProductId = createSelector(
  selectSelectedCampaign,
  (c) => c?.productId ?? null
);

export const selectCanAcceptRecurring = createSelector(
  selectSelectedCampaign,
  (c) => !!c?.isIndefinite
);

export const selectSelectedCampaignVM = createSelector(
  selectSelectedCampaign,
  (c): SelectedCampaignVM | null => {
    if (!c) return null;

    // Centavos (backend)
    const currentCents =
      (c as any).raisedAmount ?? (c as any).currentAmount ?? 0;
    const targetCents = c.targetAmount ?? 0;

    // USD (frontend)
    const currentAmount = currentCents / 100;
    const targetAmount = targetCents / 100;

    const progressPct =
      targetCents > 0
        ? Math.max(0, Math.min(100, (currentCents / targetCents) * 100))
        : 0;

    return {
      id: c.id,
      nameEn: c.nameEn,
      nameEs: c.nameEs,
      descriptionEn: c.descriptionEn,
      descriptionEs: c.descriptionEs,
      // expón en USD para usar directo en template
      currentAmount,
      targetAmount,
      progressPct,
      imageUrl: c.imageUrl,
      productId: c.productId,
      productFeeId: c.productFeeId,
    };
  }
);

// New: language-aware VM selector (returns localized name/description)
export const selectSelectedCampaignVMByLang = createSelector(
  selectSelectedCampaign,
  (c, props: { lang?: string } = {}): SelectedCampaignVM | null => {
    if (!c) return null;
    const lang = props.lang === 'es' ? 'es' : 'en';

    // Centavos (backend)
    const currentCents =
      (c as any).raisedAmount ?? (c as any).currentAmount ?? 0;
    const targetCents = c.targetAmount ?? 0;

    // USD (frontend)
    const currentAmount = currentCents / 100;
    const targetAmount = targetCents / 100;

    const progressPct =
      targetCents > 0
        ? Math.max(0, Math.min(100, (currentCents / targetCents) * 100))
        : 0;

    return {
      id: c.id,
      // localized fields (single-name/description can be consumed by template)
      nameEn: c.nameEn,
      nameEs: c.nameEs,
      descriptionEn: c.descriptionEn,
      descriptionEs: c.descriptionEs,
      // provide convenience fields for chosen language
      // (you can use nameEn/nameEs in template or adapt structure as needed)
      currentAmount,
      targetAmount,
      progressPct,
      imageUrl: c.imageUrl,
      productId: c.productId,
      productFeeId: c.productFeeId,
    };
  }
);

/* ========= POP-UP ONLY ========= */

// Solo campañas activas con isPopup = true
const asBool = (v: unknown) =>
  v === true || v === 'true' || v === 1 || v === '1';

export const selectPopupCampaigns = createSelector(selectAllCampaigns, (list) =>
  list.filter((c: any) => asBool(c?.isActive) && asBool(c?.isPopup))
);

// ¿hay más de una pop-up?
export const selectHasMultiplePopupCampaigns = createSelector(
  selectPopupCampaigns,
  (list) => list.length > 1
);

// Primera pop-up (para auto-selección si no hay selección válida)
export const selectFirstPopupCampaignId = createSelector(
  selectPopupCampaigns,
  (list) => (list.length ? list[0].id : null)
);

// Si la selección actual no es pop-up, devuelve la primera pop-up
export const selectSelectedPopupCampaign = createSelector(
  selectCampaignEntities,
  selectSelectedId,
  selectFirstPopupCampaignId,
  (entities, selectedId, fallbackId) => {
    const sel = selectedId ? entities[selectedId] : null;
    if (sel?.isActive && sel?.isPopup) return sel;
    return fallbackId ? entities[fallbackId] ?? null : null;
  }
);

// VM del seleccionado pop-up
export const selectSelectedPopupCampaignVM = createSelector(
  selectSelectedPopupCampaign,
  (c): SelectedCampaignVM | null => {
    if (!c) return null;
    const currentAmount =
      (c as any).raisedAmount ?? (c as any).currentAmount ?? 0;
    const progressPct =
      c.targetAmount > 0
        ? Math.max(0, Math.min(100, (currentAmount / c.targetAmount) * 100))
        : 0;

    return {
      id: c.id,
      nameEn: c.nameEn,
      nameEs: c.nameEs,
      descriptionEn: c.descriptionEn,
      descriptionEs: c.descriptionEs,
      targetAmount: c.targetAmount,
      currentAmount,
      progressPct,
      imageUrl: c.imageUrl,
      productId: c.productId,
      productFeeId: c.productFeeId,
    };
  }
);
